import React, { useMemo, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ACCEPTED = ".pdf,.jpg,.jpeg,.png,.webp,.heic,.heif,image/jpeg,image/jpg,image/png,image/webp,image/heic,image/heif,application/pdf";
const ACCEPTED_MIMES = new Set([
  "application/pdf", "image/jpeg", "image/jpg", "image/png",
  "image/webp", "image/heic", "image/heif",
]);
const MAX_BYTES = 10 * 1024 * 1024;
const MAX_REPORTS = 6;

const statusColor = {
  normal: "text-teal-soft border-teal-mid/25 bg-teal-mid/[0.05]",
  low: "text-coral-400 border-coral-500/30 bg-coral-500/[0.06]",
  high: "text-coral-400 border-coral-500/30 bg-coral-500/[0.06]",
  borderline: "text-lav-400 border-lav-500/30 bg-lav-500/[0.06]",
};

function StatusDot({ s }) {
  const c = s === "normal" ? "#2DD4BF" : s === "borderline" ? "#C4B5FD" : "#FB7185";
  return <span className="inline-block h-2 w-2 rounded-full" style={{ background: c }} />;
}

function inferMime(f) {
  if (f.type && ACCEPTED_MIMES.has(f.type)) return f.type;
  const n = (f.name || "").toLowerCase();
  if (n.endsWith(".pdf")) return "application/pdf";
  if (n.endsWith(".jpg") || n.endsWith(".jpeg")) return "image/jpeg";
  if (n.endsWith(".png")) return "image/png";
  if (n.endsWith(".webp")) return "image/webp";
  if (n.endsWith(".heic")) return "image/heic";
  if (n.endsWith(".heif")) return "image/heif";
  return f.type || "";
}

const parseNum = (v) => {
  if (v == null) return null;
  const m = String(v).match(/-?\d+(\.\d+)?/);
  return m ? parseFloat(m[0]) : null;
};

const scanDate = (s, fallbackIdx) => {
  if (s?.date) {
    const t = Date.parse(s.date);
    if (!Number.isNaN(t)) return t;
  }
  return s?._uploadedAt || fallbackIdx;
};

function Sparkline({ points }) {
  if (points.length < 2) return null;
  const w = 100, h = 26;
  const vals = points.map((p) => p.n).filter((n) => n != null);
  if (vals.length < 2) return null;
  const min = Math.min(...vals);
  const max = Math.max(...vals);
  const span = max - min || 1;
  const step = w / (vals.length - 1);
  const pts = vals.map((n, i) => `${(i * step).toFixed(1)},${(h - ((n - min) / span) * h).toFixed(1)}`).join(" ");
  return (
    <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`} className="opacity-90">
      <polyline points={pts} fill="none" stroke="#2DD4BF" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      {vals.map((n, i) => (
        <circle key={i} cx={i * step} cy={h - ((n - min) / span) * h} r="1.8" fill="#2DD4BF" />
      ))}
    </svg>
  );
}

function TrendArrow({ first, last }) {
  if (first == null || last == null || first === last) {
    return <span className="text-zinc-500 text-[10px]">→ flat</span>;
  }
  const up = last > first;
  const delta = last - first;
  const pct = first ? ((delta / first) * 100).toFixed(0) : "0";
  return (
    <span className="inline-flex items-center gap-1 text-[10px] text-zinc-300">
      <span className="font-mono">{up ? "▲" : "▼"}</span>
      {up ? "+" : ""}{delta.toFixed(delta % 1 === 0 ? 0 : 1)}{" "}
      <span className="text-zinc-500">({up ? "+" : ""}{pct}%)</span>
    </span>
  );
}

const fmtShortDate = (d) => {
  if (!d) return "—";
  const t = Date.parse(d);
  if (Number.isNaN(t)) return d;
  return new Date(t).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "2-digit" });
};

export default function LabIntelligence() {
  const [scans, setScans] = useState([]); // [{id, filename, ...LabScanResult, _uploadedAt}]
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [dragOver, setDragOver] = useState(false);
  const fileRef = useRef(null);

  const handleFiles = async (files) => {
    if (!files || files.length === 0) return;
    const arr = Array.from(files);
    for (const f of arr) {
      if (scans.length >= MAX_REPORTS) {
        toast.error(`Max ${MAX_REPORTS} reports for the demo.`);
        break;
      }
      await uploadOne(f);
    }
  };

  const uploadOne = async (f) => {
    const mime = inferMime(f);
    if (!ACCEPTED_MIMES.has(mime)) {
      toast.error(`${f.name}: unsupported format.`);
      return;
    }
    if (f.size > MAX_BYTES) {
      toast.error(`${f.name}: too large (max 10 MB).`);
      return;
    }
    setUploading(true);
    setProgress(6);

    const start = Date.now();
    const tick = setInterval(() => {
      const secs = (Date.now() - start) / 1000;
      setProgress((p) => Math.min(92, Math.max(p, 6 + secs * 10)));
    }, 250);

    try {
      const form = new FormData();
      form.append("file", new File([f], f.name, { type: mime }));
      const { data } = await axios.post(`${API}/lab-scan`, form, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 90000,
      });
      setProgress(100);
      setScans((prev) => [
        ...prev,
        {
          id: `${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
          filename: f.name,
          _uploadedAt: Date.now(),
          ...data,
        },
      ]);
      toast.success(`Analyzed ${f.name} · ${data.biomarker_count} biomarkers`);
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message || "Analysis failed.";
      toast.error(typeof detail === "string" ? detail : "Analysis failed.");
    } finally {
      clearInterval(tick);
      setUploading(false);
      setTimeout(() => setProgress(0), 600);
    }
  };

  const removeScan = (id) => setScans((prev) => prev.filter((s) => s.id !== id));
  const clearAll = () => {
    setScans([]);
    if (fileRef.current) fileRef.current.value = "";
  };

  const onPick = (e) => handleFiles(e.target.files);
  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    handleFiles(e.dataTransfer.files);
  };

  // Chronologically ordered scans
  const orderedScans = useMemo(() => {
    return [...scans].sort((a, b) => scanDate(a, 0) - scanDate(b, 1));
  }, [scans]);

  // Comparison table (biomarker keys shared across scans)
  const comparison = useMemo(() => {
    if (orderedScans.length === 0) return [];
    const byKey = new Map();
    orderedScans.forEach((s, i) => {
      (s.biomarkers || []).forEach((b) => {
        const k = b.key?.trim();
        if (!k) return;
        if (!byKey.has(k)) byKey.set(k, { key: k, unit: b.unit || "", reference: b.reference || null, points: [] });
        byKey.get(k).points.push({
          scanIdx: i,
          date: s.date,
          value: b.value,
          n: parseNum(b.value),
          status: b.status || "normal",
        });
      });
    });
    // Sort: multi-scan first, then by number of appearances desc, then alpha
    return Array.from(byKey.values()).sort((a, b) => {
      if (b.points.length !== a.points.length) return b.points.length - a.points.length;
      return a.key.localeCompare(b.key);
    });
  }, [orderedScans]);

  const multiScan = orderedScans.length >= 2;

  return (
    <section id="lab" data-testid="lab-section" className="relative py-24 md:py-32 overflow-hidden">
      <div className="absolute inset-0 -z-10">
        <div className="absolute inset-0 bg-grid opacity-[0.22]" />
      </div>

      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="max-w-3xl">
          <div className="text-[11px] uppercase tracking-[0.22em] text-teal-soft">05 — Lab report intelligence</div>
          <h2 className="mt-4 font-heading text-4xl sm:text-5xl tracking-tighter leading-[1.05] font-medium">
            Upload multiple reports.
            <br /><span className="gradient-text-teal-lav">See your biomarkers move over time.</span>
          </h2>
          <p className="mt-5 text-zinc-400 max-w-2xl">
            Drop 2 or more blood reports (PDF or image, any lab, any format). We extract every biomarker,
            align them by date, and show you the trend line your annual check-up misses.
          </p>
        </div>

        <div className="mt-12 grid lg:grid-cols-[1fr,1.1fr] gap-6">
          {/* Left column: upload + report list */}
          <div className="rounded-3xl border border-white/[0.08] bg-ink-800/70 p-6 sm:p-8">
            <button
              type="button"
              onClick={() => fileRef.current?.click()}
              onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={onDrop}
              disabled={uploading || scans.length >= MAX_REPORTS}
              data-testid="lab-drop-zone"
              className={`w-full rounded-2xl border-2 border-dashed p-8 text-center transition-all ${
                dragOver ? "border-teal-mid/80 bg-teal-mid/[0.10]" :
                uploading ? "border-lav-500/60 bg-lav-500/[0.05]" :
                scans.length > 0 ? "border-teal-mid/45 bg-teal-mid/[0.04] hover:bg-teal-mid/[0.08]" :
                "border-teal-mid/40 hover:border-teal-mid/70 bg-teal-mid/[0.03] hover:bg-teal-mid/[0.06]"
              } ${uploading ? "cursor-wait" : scans.length >= MAX_REPORTS ? "opacity-60 cursor-not-allowed" : "cursor-pointer"}`}
            >
              <div className="mx-auto h-11 w-11 rounded-xl border border-white/10 bg-white/[0.03] flex items-center justify-center">
                {uploading ? (
                  <svg className="animate-spin" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#C4B5FD" strokeWidth="2.4" strokeLinecap="round"><path d="M21 12a9 9 0 1 1-6.2-8.55"/></svg>
                ) : (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#2DD4BF" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M17 8l-5-5-5 5M12 3v12"/></svg>
                )}
              </div>
              <div className="mt-3 font-heading text-base tracking-tight">
                {uploading ? "Analyzing…" : scans.length === 0 ? "Drop your first report" : "Add another report"}
              </div>
              <div className="mt-1 text-xs text-zinc-500">
                {uploading ? "Extracting biomarkers · aligning by date" :
                  scans.length === 0 ? "PDF, JPG, PNG, WEBP, HEIC · up to 10 MB each · pick multiple at once" :
                  scans.length >= MAX_REPORTS ? `Max ${MAX_REPORTS} for the demo` :
                  `${scans.length}/${MAX_REPORTS} loaded · trends unlock with 2+ reports`}
              </div>
              {uploading && (
                <div className="mt-3 mx-auto max-w-xs h-1 rounded-full bg-white/[0.08] overflow-hidden">
                  <div className="h-full bg-lav-500 transition-all" style={{ width: `${progress}%` }} />
                </div>
              )}
              <input ref={fileRef} type="file" accept={ACCEPTED} multiple onChange={onPick} className="hidden" data-testid="lab-file-input" />
            </button>

            {/* Reports list */}
            <div className="mt-5">
              <div className="flex items-center justify-between mb-2">
                <div className="text-[10px] uppercase tracking-[0.22em] text-teal-soft">Your reports</div>
                {scans.length > 0 && (
                  <button onClick={clearAll} data-testid="lab-clear-btn"
                    className="text-[10px] uppercase tracking-wider text-zinc-400 hover:text-white">Clear all</button>
                )}
              </div>

              {scans.length === 0 ? (
                <div className="rounded-xl border border-dashed border-white/[0.08] p-6 text-center text-xs text-zinc-500">
                  No reports yet. Add one to preview biomarkers — or two to see trends.
                </div>
              ) : (
                <ul className="space-y-2" data-testid="lab-report-list">
                  {orderedScans.map((s, i) => (
                    <motion.li key={s.id}
                      initial={{ y: 6, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ duration: 0.35 }}
                      data-testid={`lab-report-item-${i}`}
                      className="flex items-center gap-3 rounded-xl border border-white/[0.06] bg-white/[0.02] p-3">
                      <div className="h-8 w-8 rounded-md border border-white/10 bg-white/[0.03] flex items-center justify-center shrink-0">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#2DD4BF" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><path d="M14 2v6h6"/></svg>
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="text-sm text-white truncate">{s.name || s.filename}</div>
                        <div className="text-[10px] uppercase tracking-wider text-zinc-500 truncate">
                          {fmtShortDate(s.date)} · {s.biomarker_count} biomarkers
                        </div>
                      </div>
                      <button onClick={() => removeScan(s.id)} data-testid={`lab-remove-${i}`}
                        aria-label="Remove report"
                        className="h-7 w-7 rounded-md text-zinc-500 hover:text-coral-400 hover:bg-coral-500/[0.08] transition-colors">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mx-auto"><path d="M18 6 6 18"/><path d="m6 6 12 12"/></svg>
                      </button>
                    </motion.li>
                  ))}
                </ul>
              )}
            </div>

            <div className="mt-5 text-[11px] text-zinc-500">
              ✱ Files are analyzed in-session and discarded. Nothing stored.
            </div>
          </div>

          {/* Right column: trends + locked CTA */}
          <div className="rounded-3xl border border-white/[0.08] bg-ink-800/70 p-6 sm:p-8">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-[10px] uppercase tracking-[0.22em] text-teal-soft">
                  {orderedScans.length === 0 ? "What you'll see" : multiScan ? "Biomarker trends" : "Extracted biomarkers"}
                </div>
                <div className="font-heading text-lg mt-1 tracking-tight">
                  {orderedScans.length === 0 && "Upload to begin"}
                  {orderedScans.length === 1 && (orderedScans[0].name || "Your report")}
                  {multiScan && `${orderedScans.length} reports · ${comparison.length} biomarkers`}
                </div>
              </div>
              <span className="text-[10px] font-mono text-zinc-500">any lab · any format</span>
            </div>

            <AnimatePresence mode="wait">
              {orderedScans.length === 0 ? (
                <motion.ul key="promise" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                  className="mt-6 space-y-2.5 text-sm text-zinc-300">
                  {[
                    "Every biomarker extracted, unit-normalized, graded",
                    "Aligned by report date into a personal timeline",
                    "Trend line, delta and % change per biomarker",
                    "Reference range flagged when out of bounds",
                  ].map((t) => (
                    <li key={t} className="flex items-start gap-2.5">
                      <span className="inline-flex mt-0.5 h-5 w-5 items-center justify-center rounded-full bg-teal-mid/20 text-teal-soft shrink-0">
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="m5 12 5 5L20 7"/></svg>
                      </span>
                      {t}
                    </li>
                  ))}
                </motion.ul>
              ) : (
                <motion.div key="table" initial={{ y: 8, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.4 }}
                  className="mt-5 max-h-[380px] overflow-y-auto pr-1" data-testid="lab-comparison-panel">
                  <div className="grid grid-cols-[1.1fr,auto,auto] gap-x-3 text-[10px] uppercase tracking-wider text-zinc-500 pb-2 border-b border-white/[0.05] px-1">
                    <span>Biomarker</span>
                    <span className="text-right">{multiScan ? "Trend" : "Value"}</span>
                    <span className="text-right">{multiScan ? "Δ" : "Status"}</span>
                  </div>
                  <ul className="divide-y divide-white/[0.05]">
                    {comparison.map((row, i) => {
                      const last = row.points[row.points.length - 1];
                      const first = row.points[0];
                      const hasSeries = row.points.length >= 2;
                      return (
                        <li key={row.key} className="py-3 grid grid-cols-[1.1fr,auto,auto] gap-x-3 items-center px-1"
                            data-testid={`lab-trend-${i}`}>
                          <div className="min-w-0">
                            <div className="text-sm text-white truncate">{row.key}</div>
                            <div className="text-[10px] text-zinc-500 mt-0.5">
                              {row.points.map((p, idx) => (
                                <span key={idx} className="mr-2">
                                  <span className="text-zinc-400">{p.value}</span>
                                  {row.unit && <span className="text-zinc-600"> {row.unit}</span>}
                                  {p.date && <span className="text-zinc-600"> · {fmtShortDate(p.date)}</span>}
                                  {idx < row.points.length - 1 && <span className="text-zinc-700"> →</span>}
                                </span>
                              ))}
                            </div>
                          </div>
                          <div className="justify-self-end">
                            {hasSeries ? (
                              <Sparkline points={row.points} />
                            ) : (
                              <div className="font-heading text-sm tracking-tight text-white">
                                {last.value}{row.unit && <span className="text-[10px] text-zinc-500 font-body ml-1">{row.unit}</span>}
                              </div>
                            )}
                          </div>
                          <div className="justify-self-end text-right min-w-[70px]">
                            {hasSeries ? (
                              <TrendArrow first={first.n} last={last.n} />
                            ) : (
                              <span className={`inline-flex items-center gap-1.5 text-[10px] uppercase tracking-wider rounded-full border px-2 py-0.5 ${statusColor[last.status] || statusColor.normal}`}>
                                <StatusDot s={last.status} />{last.status}
                              </span>
                            )}
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Locked insight CTA */}
            {orderedScans.length > 0 && (
              <motion.div initial={{ y: 8, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ duration: 0.4 }}
                className="mt-6 rounded-2xl border border-lav-500/30 bg-gradient-to-br from-lav-500/[0.08] via-ink-800/40 to-teal-mid/[0.05] p-5"
                data-testid="lab-locked-cta">
                <div className="flex items-start gap-3">
                  <div className="h-9 w-9 rounded-md bg-lav-500/20 flex items-center justify-center shrink-0">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#C4B5FD" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                      <rect x="5" y="11" width="14" height="9" rx="2" />
                      <path d="M8 11V8a4 4 0 0 1 8 0v3" />
                    </svg>
                  </div>
                  <div className="min-w-0">
                    <div className="text-[10px] uppercase tracking-[0.22em] text-lav-400">Actionable insight · beta only</div>
                    <div className="mt-1 text-sm sm:text-base text-zinc-100 leading-relaxed">
                      Your trends are ready. What they <span className="italic">mean</span> for your body — the causal chain to food, sleep and mood —
                      unlocks inside the app.
                    </div>
                    <a href="#waitlist"
                       data-testid="lab-cta-waitlist"
                       className="mt-4 inline-flex items-center gap-2 rounded-full px-4 py-2.5 text-sm font-medium text-ink-900 bg-teal-soft hover:bg-teal-mid transition-all glow-teal">
                      Join the beta to unlock insight
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14M13 5l7 7-7 7"/></svg>
                    </a>
                  </div>
                </div>
              </motion.div>
            )}

            <div className="mt-6 flex items-center justify-between text-[11px] text-zinc-500">
              <span>Biomarkers extracted from any lab, any format</span>
              <span className="font-mono">v1 · beta</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
